import { useState, useEffect } from 'react'
import { Headphones, Loader2 } from 'lucide-react'
import Modal from '../UI/Modal'
import { useT } from '../../hooks/useTranslation'
import { useHaptic } from '../../hooks/useHaptic'
import { useTelegram } from '../../hooks/useTelegram'
import { sendSupportMessage } from '../../services/api'
import { SUPPORT_TELEGRAM_HANDLE } from '../../constants/support'

const OPTION_KEYS = ['supportOpt1', 'supportOpt2', 'supportOpt3']

// Глобальная кнопка поддержки (🎧 в шапке): пользователь выбирает вариант
// проблемы (или пишет свой), жмёт «Написать в поддержку» — и выбранный
// вариант автоматически уходит в чат поддержки через бэкенд
// (POST /api/support/send). Пользователю ничего копировать не нужно.
export default function SupportModal({ onClose }) {
  const t = useT()
  const haptic = useHaptic()
  const { user } = useTelegram()
  const [option, setOption] = useState(null)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [failed, setFailed] = useState(false)
  // Кулдаун между обращениями (секунды). Бэкенд жёстко держит 30 минут,
  // фронт просто показывает обратный отсчёт, чтобы было понятно, когда
  // можно отправить снова.
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setInterval(() => setCooldown(c => (c > 1 ? c - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [cooldown > 0])

  const fmt = (sec) => {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }

  const send = async () => {
    if (sending || cooldown > 0) return
    haptic.medium()
    const chosen = option ? t[OPTION_KEYS[option - 1]] : (text.trim() || t.supportOpt3)
    const uid = user?.id ?? ''
    const uname = user?.username ? `@${user.username}` : ''
    const lines = [t.supportMsgGeneralHello, '', chosen]
    if (text.trim() && option) lines.push(`${t.supportMsgDetails}: ${text.trim()}`)
    lines.push('', `${t.supportMsgUser}: ${uid}${uname ? ` (${uname})` : ''}`)
    const message = lines.join('\n')

    setSending(true)
    setSent(false)
    setFailed(false)
    try {
      const r = await sendSupportMessage(message)
      // Сервер вернул cooldownMin — стартуем отсчёт сразу, не дожидаясь 429.
      if (r?.cooldownMin) setCooldown(r.cooldownMin * 60)
      setSent(true)
    } catch (e) {
      if (e?.retryAfter) setCooldown(Math.ceil(e.retryAfter))
      else setFailed(true)
    } finally {
      setSending(false)
    }
  }

  const busy = sending || cooldown > 0
  const footer = (
    <button
      onClick={send}
      disabled={busy}
      style={{
        width: '100%', padding: '14px', borderRadius: 14,
        background: busy ? 'rgba(52,211,153,0.35)' : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
        color: '#fff', fontSize: 15, fontWeight: 700,
        border: 'none', cursor: busy ? 'default' : 'pointer',
        transition: 'opacity 0.2s',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}
    >
      {sending && <Loader2 size={16} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />}
      {sending ? t.supportSending : t.supportContact}
    </button>
  )

  return (
    <Modal title={t.supportTitle} onClose={onClose} footer={footer}>
      <div style={{ padding: '16px 0 8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center', marginBottom: 18 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 20px rgba(16,185,129,0.35)',
            }}
          >
            <Headphones size={26} color="#fff" />
          </div>
          <p style={{ fontSize: 13, color: '#8E8E93', lineHeight: 1.6, maxWidth: 300, margin: 0 }}>
            {t.supportIntro}
          </p>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#34D399' }}>
            {SUPPORT_TELEGRAM_HANDLE}
          </div>
        </div>

        {/* Варианты проблемы */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {[1, 2, 3].map(i => (
            <button
              key={i}
              onClick={() => { setOption(i); setSent(false); setFailed(false) }}
              style={{
                textAlign: 'left', padding: '12px 14px', borderRadius: 12, cursor: 'pointer',
                fontSize: 13.5, lineHeight: 1.4, color: '#fff',
                background: option === i ? 'rgba(52,211,153,0.12)' : 'rgba(255,255,255,0.05)',
                border: option === i ? '1px solid #34D399' : '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {t[OPTION_KEYS[i - 1]]}
            </button>
          ))}
        </div>

        {/* Свой текст */}
        <textarea
          value={text}
          onChange={e => { setText(e.target.value); setSent(false); setFailed(false) }}
          placeholder={t.supportPlaceholder}
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box', resize: 'none', padding: '12px 14px',
            borderRadius: 12, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff', fontSize: 13.5, lineHeight: 1.5, outline: 'none',
          }}
        />

        {/* Статус отправки */}
        {sent && (
          <div style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 10, padding: '10px 12px', marginTop: 12, fontSize: 12.5, color: '#34D399', lineHeight: 1.5 }}>
            {t.supportSentOk}
          </div>
        )}
        {failed && (
          <div style={{ background: 'rgba(255,107,53,0.08)', borderRadius: 10, padding: '10px 12px', marginTop: 12, border: '1px solid rgba(255,107,53,0.2)', fontSize: 12.5, color: '#F59E0B', lineHeight: 1.5 }}>
            {t.supportSendFail}
          </div>
        )}
        {cooldown > 0 && (
          <div style={{ background: 'rgba(245,158,11,0.08)', borderRadius: 10, padding: '10px 12px', marginTop: 12, border: '1px solid rgba(245,158,11,0.25)', fontSize: 12.5, color: '#F59E0B', lineHeight: 1.5 }}>
            ⏳ {t.supportCooldown.replace('{min}', Math.ceil(cooldown / 60))} · {fmt(cooldown)}
          </div>
        )}
      </div>
    </Modal>
  )
}
